import type { PostStatusInput } from "@postdaily/api/schemas/posts";
import {
  Alert,
  AlertDescription,
  AlertTitle,
} from "@postdaily/ui/components/alert";
import { Button, buttonVariants } from "@postdaily/ui/components/button";
import {
  Empty,
  EmptyContent,
  EmptyDescription,
  EmptyHeader,
  EmptyTitle,
} from "@postdaily/ui/components/empty";
import {
  InputGroup,
  InputGroupAddon,
  InputGroupButton,
  InputGroupInput,
} from "@postdaily/ui/components/input-group";
import {
  NativeSelect,
  NativeSelectOption,
} from "@postdaily/ui/components/native-select";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@postdaily/ui/components/table";
import { onNativeSelectChange } from "@postdaily/ui/lib/option-value";
import { keepPreviousData, useQuery } from "@tanstack/react-query";
import {
  createFileRoute,
  Link,
  Outlet,
  redirect,
  useRouterState,
} from "@tanstack/react-router";
import dayjs from "dayjs";
import {
  ArrowDownIcon,
  ArrowUpIcon,
  ChevronLeftIcon,
  ChevronRightIcon,
  SearchIcon,
  XIcon,
} from "lucide-react";
import {
  parseAsInteger,
  parseAsString,
  parseAsStringEnum,
  useQueryStates,
} from "nuqs";
import { useEffect, useState } from "react";
import { useDebouncedCallback } from "use-debounce";

import { useComposer } from "@/components/composer/composer-dialog-provider";
import { PageHeader } from "@/components/page-header";
import { FailedPostsList } from "@/components/posts/failed-posts-list";
import { PostStatusBadge } from "@/components/posts/post-status-badge";
import {
  CardSkeletonList,
  TableLoadingOverlay,
  TableSkeletonRows,
} from "@/components/posts/posts-table-parts";
import { formatScheduledAt } from "@/components/scheduling/schedule-time";
import { useCurrentWorkspace } from "@/hooks/use-current-workspace";
import { postDisplayTitle } from "@/lib/post-title";
import { useTRPC } from "@/utils/trpc";

export const Route = createFileRoute("/(app)/posts")({
  // The posts table is hidden for now — the calendar is the primary surface.
  // The component below is preserved so an improved version can be re-enabled
  // by removing this `beforeLoad`. The redirect also covers the nested
  // `/posts/$postId` detail route. Any lingering link to a posts page lands on
  // the calendar instead of a dead end.
  beforeLoad: ({ location }) => {
    // A post link (/posts/<id> — what AI apps linked to before 2026-09-29)
    // opens that post's details over the Publish list, where notification
    // emails go; this parent redirect runs before the detail route's own.
    const postId = /^\/posts\/([^/?#]+)/.exec(location.pathname)?.[1];

    if (postId) {
      throw redirect({
        href: `/calendar/week?layout=list&status=all&post=${encodeURIComponent(decodeURIComponent(postId))}`,
        replace: true,
      });
    }

    throw redirect({ to: "/calendar", replace: true });
  },
  component: RouteComponent,
});

const statusOptions = [
  "needs_attention",
  "draft",
  "scheduled",
  "publishing",
  "published",
  "partially_failed",
  "failed",
  "canceled",
] as const;
const statusFilterValues = [...statusOptions];

type StatusFilter = (typeof statusOptions)[number];

const statusLabels = {
  canceled: "Canceled",
  draft: "Draft",
  failed: "Failed",
  needs_attention: "Needs attention",
  partially_failed: "Partially failed",
  published: "Published",
  publishing: "Publishing",
  scheduled: "Scheduled",
} satisfies Record<StatusFilter, string>;

/** In menu order; also the `?sortBy=` values the route parses. */
const sortOptions = [
  "updatedAt",
  "createdAt",
  "scheduledAt",
  "status",
] as const;
const sortLabels = {
  updatedAt: "Updated",
  createdAt: "Created",
  scheduledAt: "Scheduled",
  status: "Status",
} satisfies Record<(typeof sortOptions)[number], string>;

const pageSizeOptions = [10, 20, 50] as const;
const POSTS_COLUMN_COUNT = 6;

function RouteComponent() {
  const pathname = useRouterState({
    select: (state) => state.location.pathname,
  });
  const { currentWorkspace } = useCurrentWorkspace();
  const trpc = useTRPC();
  const { openComposer } = useComposer();
  const [filters, setFilters] = useQueryStates({
    page: parseAsInteger.withDefault(1),
    pageSize: parseAsInteger.withDefault(20),
    search: parseAsString.withDefault(""),
    sortBy: parseAsStringEnum([...sortOptions]).withDefault("updatedAt"),
    sortDirection: parseAsStringEnum(["asc", "desc"]).withDefault("desc"),
    status: parseAsStringEnum(statusFilterValues).withDefault("draft"),
  });

  // Local, immediately-responsive search value. The committed value in the URL
  // (and therefore the query) is updated on a debounce so typing stays smooth
  // and we don't refetch on every keystroke.
  const [searchInput, setSearchInput] = useState(filters.search);
  const commitSearch = useDebouncedCallback((value: string) => {
    void setFilters({ page: 1, search: value });
  }, 350);

  // Keep the input in sync when the URL changes from elsewhere (back/forward,
  // clearing filters, etc.).
  useEffect(() => {
    setSearchInput(filters.search);
  }, [filters.search]);

  const isFailuresFilter = filters.status === "needs_attention";
  // SAFETY: `filters.status` comes from the route's validated search, whose
  // values are the post statuses plus the synthetic "needs_attention" tab. That
  // one arm is handled above, so what reaches here is always a real status.
  const selectedPostStatus = (
    isFailuresFilter ? "draft" : filters.status
  ) as PostStatusInput;

  const postsQuery = useQuery(
    trpc.posts.list.queryOptions(
      {
        page: filters.page,
        pageSize: filters.pageSize,
        search: filters.search,
        sortBy: filters.sortBy,
        sortDirection: filters.sortDirection,
        status: [selectedPostStatus],
        workspaceId: currentWorkspace?.id ?? "",
      },
      {
        enabled: Boolean(currentWorkspace?.id) && !isFailuresFilter,
        placeholderData: keepPreviousData,
      },
    ),
  );
  const failuresQuery = useQuery(
    trpc.posts.listFailures.queryOptions(
      {
        page: filters.page,
        pageSize: filters.pageSize,
        search: filters.search,
        sortBy:
          filters.sortBy === "scheduledAt" || filters.sortBy === "updatedAt"
            ? filters.sortBy
            : "updatedAt",
        sortDirection: filters.sortDirection,
        workspaceId: currentWorkspace?.id ?? "",
      },
      {
        enabled: Boolean(currentWorkspace?.id) && isFailuresFilter,
        placeholderData: keepPreviousData,
      },
    ),
  );

  if (pathname !== "/posts") {
    return <Outlet />;
  }

  const activeQuery = isFailuresFilter ? failuresQuery : postsQuery;
  const posts = postsQuery.data;
  const failedPosts = failuresQuery.data;
  const currentPage = isFailuresFilter ? failedPosts : posts;
  const items = currentPage?.items ?? [];
  // Narrowed to the posts-list shape for the default (non-failures) table.
  const postItems = posts?.items ?? [];

  // `isPending` (no data yet for this filter combo) → skeleton rows.
  // `isFetching` after we already have data → keep rows, dim with an overlay.
  const isInitialLoading = activeQuery.isPending;
  const isRefetching = activeQuery.isFetching && !activeQuery.isPending;
  const isError = activeQuery.isError;
  const isEmpty = !isInitialLoading && !isError && items.length === 0;

  const hasActiveFilters =
    Boolean(filters.search) || filters.status !== "draft";
  const workspaceName = currentWorkspace?.name;

  const total = currentPage?.total ?? 0;
  const page = currentPage?.page ?? filters.page;
  const pageSize = currentPage?.pageSize ?? filters.pageSize;
  const totalPages = Math.max(currentPage?.totalPages ?? 1, 1);
  const rangeFrom = total === 0 ? 0 : (page - 1) * pageSize + 1;
  const rangeTo = Math.min(page * pageSize, total);
  const canPrev = page > 1;
  const canNext = page < totalPages;

  return (
    <div className="flex flex-col gap-6">
      <PageHeader
        title="Posts"
        description={
          isFailuresFilter
            ? `Failed posts that need attention${workspaceName ? ` for ${workspaceName}` : ""}.`
            : `Posts${workspaceName ? ` for ${workspaceName}` : ""}.`
        }
        actions={
          <Button size="sm" onClick={() => openComposer()}>
            Create post
          </Button>
        }
      />

      {/* Toolbar — always mounted so search/filters never disappear. */}
      <div className="flex flex-col gap-3 sm:flex-row sm:items-center">
        <InputGroup className="sm:max-w-xs">
          <InputGroupAddon>
            <SearchIcon />
          </InputGroupAddon>
          <InputGroupInput
            value={searchInput}
            placeholder="Search posts"
            aria-label="Search posts"
            onChange={(event) => {
              const value = event.currentTarget.value;
              setSearchInput(value);
              commitSearch(value);
            }}
          />
          {searchInput ? (
            <InputGroupAddon align="inline-end">
              <InputGroupButton
                size="icon-xs"
                aria-label="Clear search"
                onClick={() => {
                  setSearchInput("");
                  commitSearch.cancel();
                  void setFilters({ page: 1, search: "" });
                }}
              >
                <XIcon />
              </InputGroupButton>
            </InputGroupAddon>
          ) : null}
        </InputGroup>

        <div className="flex flex-wrap items-center gap-2 sm:ml-auto">
          <NativeSelect
            value={filters.status}
            aria-label="Post status"
            onChange={onNativeSelectChange(statusOptions, (status) => {
              void setFilters({ page: 1, status });
            })}
          >
            {statusOptions.map((status) => (
              <NativeSelectOption key={status} value={status}>
                {statusLabels[status]}
              </NativeSelectOption>
            ))}
          </NativeSelect>

          <NativeSelect
            value={filters.sortBy}
            aria-label="Sort posts"
            onChange={onNativeSelectChange(sortOptions, (sortBy) => {
              void setFilters({ page: 1, sortBy });
            })}
          >
            {sortOptions.map((value) => (
              <NativeSelectOption key={value} value={value}>
                {sortLabels[value]}
              </NativeSelectOption>
            ))}
          </NativeSelect>

          <Button
            type="button"
            variant="outline"
            size="icon"
            aria-label={`Sort ${filters.sortDirection === "asc" ? "ascending" : "descending"}`}
            title={filters.sortDirection === "asc" ? "Ascending" : "Descending"}
            onClick={() => {
              void setFilters({
                page: 1,
                sortDirection: filters.sortDirection === "asc" ? "desc" : "asc",
              });
            }}
          >
            {filters.sortDirection === "asc" ? (
              <ArrowUpIcon />
            ) : (
              <ArrowDownIcon />
            )}
          </Button>
        </div>
      </div>

      {isError ? (
        <Alert variant="destructive">
          <AlertTitle>Posts unavailable</AlertTitle>
          <AlertDescription>{activeQuery.error?.message}</AlertDescription>
        </Alert>
      ) : isFailuresFilter ? (
        isInitialLoading || items.length > 0 ? (
          <FailedPostsList
            posts={failedPosts}
            isLoading={isInitialLoading}
            isFetching={isRefetching}
          />
        ) : (
          <Empty className="rounded-xl border">
            <EmptyHeader>
              <EmptyTitle>No failed posts</EmptyTitle>
              <EmptyDescription>
                Failed and partially failed posts will appear here.
              </EmptyDescription>
            </EmptyHeader>
            <EmptyContent>
              <Link
                to="/calendar/$view"
                params={{ view: "month" }}
                className={buttonVariants({ variant: "outline" })}
              >
                View calendar
              </Link>
            </EmptyContent>
          </Empty>
        )
      ) : isEmpty ? (
        <Empty className="rounded-xl border">
          <EmptyHeader>
            <EmptyTitle>
              {hasActiveFilters
                ? "No posts match these filters"
                : "Create your first post"}
            </EmptyTitle>
            <EmptyDescription>
              {hasActiveFilters
                ? "Adjust the search or status filter."
                : "Drafts you save from the composer will appear here."}
            </EmptyDescription>
          </EmptyHeader>
          <EmptyContent>
            {hasActiveFilters ? (
              <Button
                variant="outline"
                onClick={() => {
                  setSearchInput("");
                  commitSearch.cancel();
                  void setFilters({
                    page: 1,
                    search: "",
                    status: "draft",
                  });
                }}
              >
                Clear filters
              </Button>
            ) : (
              <Button variant="outline" onClick={() => openComposer()}>
                Create post
              </Button>
            )}
          </EmptyContent>
        </Empty>
      ) : (
        <div className="relative overflow-hidden rounded-xl border bg-card">
          {/* Mobile: stacked cards */}
          <div className="flex flex-col gap-3 p-3 md:hidden">
            {isInitialLoading ? (
              <CardSkeletonList />
            ) : (
              postItems.map((post) => (
                <Link
                  key={post.id}
                  to="/posts/$postId"
                  params={{ postId: post.id }}
                  className="flex flex-col gap-2 rounded-lg border bg-background p-4 transition-colors hover:bg-muted/50"
                >
                  <div className="flex items-start justify-between gap-2">
                    <span className="min-w-0 flex-1 truncate font-medium">
                      {postDisplayTitle(post)}
                    </span>
                    <PostStatusBadge status={post.status} />
                  </div>
                  {post.baseText ? (
                    <p className="line-clamp-2 text-muted-foreground text-sm">
                      {post.baseText}
                    </p>
                  ) : null}
                  <div className="flex flex-wrap items-center gap-x-2 gap-y-1 text-muted-foreground text-xs">
                    <span>
                      {post.scheduledAt
                        ? formatScheduledAt(
                            new Date(post.scheduledAt),
                            post.timezone ?? "UTC",
                          )
                        : "Not scheduled"}
                    </span>
                    <span aria-hidden>·</span>
                    <span>
                      {post.targetCount} target
                      {post.targetCount === 1 ? "" : "s"}
                    </span>
                    {post.mediaCount > 0 ? (
                      <>
                        <span aria-hidden>·</span>
                        <span>{post.mediaCount} media</span>
                      </>
                    ) : null}
                  </div>
                </Link>
              ))
            )}
          </div>

          {/* Desktop: table */}
          <div className="hidden overflow-x-auto md:block">
            <Table>
              <TableHeader className="[&_th]:h-11 [&_th]:bg-muted/40 [&_th]:font-medium [&_th]:text-muted-foreground">
                <TableRow className="hover:bg-transparent">
                  <TableHead>Post</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead>Scheduled</TableHead>
                  <TableHead className="text-right">Targets</TableHead>
                  <TableHead className="text-right">Media</TableHead>
                  <TableHead className="text-right">Updated</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {isInitialLoading ? (
                  <TableSkeletonRows rows={8} columns={POSTS_COLUMN_COUNT} />
                ) : (
                  postItems.map((post) => (
                    <TableRow
                      key={post.id}
                      className="border-border/50 transition-colors hover:bg-muted/40"
                    >
                      <TableCell className="max-w-[18rem]">
                        <Link
                          to="/posts/$postId"
                          params={{ postId: post.id }}
                          className="flex min-w-0 flex-col gap-0.5"
                        >
                          <span className="truncate font-medium">
                            {postDisplayTitle(post)}
                          </span>
                          <span className="truncate text-muted-foreground text-xs">
                            {post.baseText || "Media-only draft"}
                          </span>
                        </Link>
                      </TableCell>
                      <TableCell>
                        <PostStatusBadge status={post.status} />
                      </TableCell>
                      <TableCell className="whitespace-nowrap text-muted-foreground">
                        {post.scheduledAt
                          ? formatScheduledAt(
                              new Date(post.scheduledAt),
                              post.timezone ?? "UTC",
                            )
                          : "Not scheduled"}
                      </TableCell>
                      <TableCell className="text-right tabular-nums">
                        {post.targetCount}
                      </TableCell>
                      <TableCell className="text-right tabular-nums">
                        {post.mediaCount}
                      </TableCell>
                      <TableCell className="whitespace-nowrap text-right text-muted-foreground tabular-nums">
                        {dayjs(post.updatedAt).format("MMM D, YYYY h:mm A")}
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </div>

          {isRefetching ? <TableLoadingOverlay /> : null}
        </div>
      )}

      {/* Pagination footer — mounted whenever there is a result set. */}
      {!isError && (isInitialLoading || total > 0) ? (
        <div className="flex flex-col-reverse items-center justify-between gap-3 sm:flex-row">
          <p className="text-muted-foreground text-sm">
            {isInitialLoading ? (
              "Loading posts…"
            ) : (
              <>
                Showing{" "}
                <span className="font-medium text-foreground tabular-nums">
                  {rangeFrom}–{rangeTo}
                </span>{" "}
                of{" "}
                <span className="font-medium text-foreground tabular-nums">
                  {total}
                </span>
              </>
            )}
          </p>

          <div className="flex items-center gap-2">
            <NativeSelect
              size="sm"
              value={String(filters.pageSize)}
              aria-label="Rows per page"
              disabled={isInitialLoading}
              onChange={(event) => {
                void setFilters({
                  page: 1,
                  pageSize: Number(event.currentTarget.value),
                });
              }}
            >
              {pageSizeOptions.map((size) => (
                <NativeSelectOption key={size} value={String(size)}>
                  {size} / page
                </NativeSelectOption>
              ))}
            </NativeSelect>

            <span className="whitespace-nowrap text-muted-foreground text-sm tabular-nums">
              Page {page} of {totalPages}
            </span>

            <Button
              type="button"
              variant="outline"
              size="icon"
              aria-label="Previous page"
              disabled={isInitialLoading || !canPrev}
              onClick={() => {
                void setFilters({ page: Math.max(page - 1, 1) });
              }}
            >
              <ChevronLeftIcon />
            </Button>
            <Button
              type="button"
              variant="outline"
              size="icon"
              aria-label="Next page"
              disabled={isInitialLoading || !canNext}
              onClick={() => {
                void setFilters({ page: page + 1 });
              }}
            >
              <ChevronRightIcon />
            </Button>
          </div>
        </div>
      ) : null}
    </div>
  );
}
